import React from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Text,
} from '../../../../utils/chakraMonomorphic'
import {RouterLink as Link} from '../../../../utils/chakraRouterLink'

import {
  FaceOffChange,
  FaceOffFace,
  FaceOffVerdictValue,
} from '../../../../services/coreApi/faceoff'
import {buildAlbumPhotoUrl, isLinkableId} from '../../../../utils/photoDeepLink'
import copy from './copy.json'
import {fillCopy} from './constants'
import {formatDelta, formatScore, faceState, scanName} from './faceOffFaces'
import {BOX_COLORS, FaceOffLightbox, FaceOffPhoto} from './FaceOffPhoto'

/** The face a card shows: `after`, or `before` when `after` is null. */
export const cardFace = (change: FaceOffChange): FaceOffFace | null =>
  change.after ?? change.before

const scoreLine = (face: FaceOffFace | null): string =>
  face ? `${formatScore(face.score)} ${faceState(face)}` : faceState(face)

const VERDICT_ORDER: FaceOffVerdictValue[] = ['accept', 'reject', 'unsure']

/** The album link, or plain text for a photo from another camp. */
export const AlbumLink: React.FC<{
  face: FaceOffFace
  clientId: number
  testId: string
}> = ({face, clientId, testId}) => {
  const {albumId} = face
  if (
    face.clientId === clientId &&
    typeof albumId === 'number' &&
    isLinkableId(albumId)
  ) {
    return (
      <Link
        as={ReactRouterLink}
        to={buildAlbumPhotoUrl(albumId, face.photoId)}
        color='var(--fo-link)'
        textDecoration='underline'
        data-testid={`${testId}-album-link`}>
        {copy.diff.openAlbum}
      </Link>
    )
  }
  // Plain text: a link would open another camp's album under this one.
  return (
    <Text fontSize='sm' data-testid={`${testId}-album-text`}>
      {fillCopy(copy.diff.camp, {clientId: face.clientId})}
      {typeof albumId === 'number'
        ? `, ${fillCopy(copy.diff.album, {albumId})}`
        : ''}
    </Text>
  )
}

export interface FaceOffFaceCardProps {
  change: FaceOffChange
  // The signed-in camp. Album links open only for this camp's photos.
  clientId: number
  testId: string
  // Flips only: the card takes focus and shows its verdict.
  verdict?: FaceOffVerdictValue | null
  saving?: boolean
  saveError?: boolean
  note?: string
  onNoteChange?: (note: string) => void
  onVerdict?: (verdict: FaceOffVerdictValue) => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
}

/**
 * One flip or shift: the photo with the face boxed beside the camper's
 * training photo, the score before and after, and for a flip its verdict.
 */
export const FaceOffFaceCard = React.forwardRef<
  HTMLDivElement,
  FaceOffFaceCardProps
>(
  (
    {
      change,
      clientId,
      testId,
      verdict,
      saving,
      saveError,
      note,
      onNoteChange,
      onVerdict,
      onKeyDown,
    },
    ref,
  ) => {
    const face = cardFace(change)
    const isFlip = onKeyDown !== undefined
    const [enlarged, setEnlarged] = React.useState(false)
    const verdictLabel = verdict
      ? copy.diff.verdicts[verdict]
      : copy.diff.noVerdict
    const personId = face?.personId ?? ''
    const boxes = face?.bbox
      ? [
          {
            bbox: face.bbox,
            color: isFlip ? BOX_COLORS.flip : BOX_COLORS.recorded,
          },
        ]
      : []
    const delta = formatDelta(change.scoreDelta)

    return (
      <Box
        ref={ref}
        borderWidth='1px'
        borderColor={isFlip ? 'var(--fo-red-line)' : 'var(--fo-amber-line)'}
        borderRadius='md'
        p={4}
        mb={4}
        tabIndex={isFlip ? 0 : undefined}
        onKeyDown={onKeyDown}
        aria-label={
          isFlip
            ? fillCopy(copy.diff.cardLabel, {personId, verdict: verdictLabel})
            : undefined
        }
        _focus={{outline: '3px solid', outlineColor: 'var(--fo-accent)'}}
        data-testid={testId}>
        <Flex gap={3} alignItems='center' mb={3} flexWrap='wrap'>
          <Badge colorScheme={change.kind === 'regression' ? 'red' : 'green'}>
            {change.kind}
          </Badge>
          <Text fontWeight='bold'>
            {fillCopy(copy.diff.person, {personId})}
          </Text>
          <Text fontSize='sm' data-testid={`${testId}-scores`}>
            {copy.diff.before} {scoreLine(change.before)}, {copy.diff.after}{' '}
            {scoreLine(change.after)}
            {delta && ` ${fillCopy(copy.diff.scoreDelta, {delta})}`}
          </Text>
          {face && (
            <Text fontSize='sm' color='var(--fo-muted)'>
              {fillCopy(copy.diff.scan, {scan: scanName(face.path)})}
            </Text>
          )}
        </Flex>
        <Flex gap={4} mb={3} alignItems='flex-start'>
          <Box flex='2'>
            <FaceOffPhoto
              src={face?.photoUrl}
              alt={fillCopy(copy.diff.person, {personId})}
              boxes={boxes}
              maxH='60vh'
              onClick={
                face?.photoUrl ? (): void => setEnlarged(true) : undefined
              }
              testId={`${testId}-photo`}
            />
          </Box>
          <Box flex='1'>
            <FaceOffPhoto
              src={face?.candidateUrl}
              alt={copy.diff.training}
              boxes={[]}
              maxH='60vh'
              onClick={
                face?.photoUrl ? (): void => setEnlarged(true) : undefined
              }
              testId={`${testId}-candidate`}
            />
          </Box>
        </Flex>
        {face && <AlbumLink face={face} clientId={clientId} testId={testId} />}
        {isFlip && (
          <Flex gap={3} mt={3} alignItems='center' flexWrap='wrap'>
            {onVerdict &&
              VERDICT_ORDER.map((v) => (
                <Button
                  key={v}
                  size='sm'
                  variant={verdict === v ? 'solid' : 'outline'}
                  colorScheme={verdict === v ? 'blue' : undefined}
                  tabIndex={-1}
                  onClick={(): void => onVerdict(v)}
                  data-testid={`${testId}-verdict-${v}`}>
                  {copy.diff.verdicts[v]}
                </Button>
              ))}
            {onNoteChange && (
              <Input
                size='sm'
                maxW='260px'
                placeholder={copy.diff.notePlaceholder}
                value={note ?? ''}
                tabIndex={-1}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  onNoteChange(e.target.value)
                }
                data-testid={`${testId}-note`}
              />
            )}
            <Text
              fontSize='sm'
              fontWeight='bold'
              data-testid={`${testId}-verdict`}>
              {verdictLabel}
            </Text>
            {saving && <Text fontSize='sm'>{copy.diff.saving}</Text>}
            {saveError && (
              <Text
                fontSize='sm'
                color='var(--fo-red)'
                data-testid={`${testId}-save-error`}>
                {copy.diff.saveError}
              </Text>
            )}
          </Flex>
        )}
        {enlarged && face?.photoUrl && (
          <FaceOffLightbox
            src={face.photoUrl}
            alt={fillCopy(copy.diff.person, {personId})}
            boxes={boxes}
            side={
              face.candidateUrl
                ? {src: face.candidateUrl, alt: copy.diff.training}
                : null
            }
            onClose={(): void => setEnlarged(false)}
          />
        )}
      </Box>
    )
  },
)

FaceOffFaceCard.displayName = 'FaceOffFaceCard'
